import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axiosInstance';

// Async thunks
export const fetchWarehouses = createAsyncThunk(
  'warehouse/fetchWarehouses',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/api/warehouse');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch warehouses');
    }
  }
);

export const fetchWarehouseById = createAsyncThunk(
  'warehouse/fetchWarehouseById',
  async (warehouseId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/warehouse/${warehouseId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch warehouse');
    }
  }
);

export const createWarehouse = createAsyncThunk(
  'warehouse/createWarehouse',
  async (warehouseData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/api/warehouse', warehouseData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create warehouse');
    }
  }
);

export const updateWarehouse = createAsyncThunk(
  'warehouse/updateWarehouse',
  async ({ warehouseId, warehouseData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/api/warehouse/${warehouseId}`, warehouseData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update warehouse');
    }
  }
);

export const deleteWarehouse = createAsyncThunk(
  'warehouse/deleteWarehouse',
  async (warehouseId, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/api/warehouse/${warehouseId}`);
      return warehouseId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete warehouse');
    }
  }
);

export const fetchWarehouseInventory = createAsyncThunk(
  'warehouse/fetchWarehouseInventory',
  async ({ warehouseId, q = '', page = 1, limit = 25 }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (q) params.append('q', q);
      if (page) params.append('page', page);
      if (limit) params.append('limit', limit);
      const response = await axiosInstance.get(`/api/warehouse/${warehouseId}/inventory?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch warehouse inventory');
    }
  }
);

export const addProductToWarehouse = createAsyncThunk(
  'warehouse/addProductToWarehouse',
  async ({ warehouseId, productData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/api/warehouse/${warehouseId}/inventory`, productData);
      return { warehouseId, product: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add product to warehouse');
    }
  }
);

export const updateWarehouseProduct = createAsyncThunk(
  'warehouse/updateWarehouseProduct',
  async ({ warehouseId, productId, productData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/api/warehouse/${warehouseId}/inventory/${productId}`, productData);
      return { warehouseId, product: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update warehouse product');
    }
  }
);

export const fetchWarehouseStats = createAsyncThunk(
  'warehouse/fetchWarehouseStats',
  async (warehouseId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/warehouse/${warehouseId}/stats`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch warehouse stats');
    }
  }
);

export const bulkUploadWarehouseProducts = createAsyncThunk(
  'warehouse/bulkUploadWarehouseProducts',
  async ({ warehouseId, products }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/api/warehouse/${warehouseId}/inventory/bulk-upload`, {
        products
      });
      return { warehouseId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to bulk upload products to warehouse');
    }
  }
);

export const bulkUpdateWarehouseInventory = createAsyncThunk(
  'warehouse/bulkUpdateWarehouseInventory',
  async ({ warehouseId, inventoryUpdates }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/api/warehouse/${warehouseId}/inventory/bulk-update`, {
        inventoryUpdates,
      });
      return { warehouseId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update warehouse inventory');
    }
  }
);

// Initial state
const initialState = {
  warehouses: [],
  currentWarehouse: null,
  warehouseInventory: null,
  warehouseStats: null,
  loading: false,
  error: null,
  success: null,
};

// Slice
const warehouseSlice = createSlice({
  name: 'warehouse',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    setCurrentWarehouse: (state, action) => {
      state.currentWarehouse = action.payload;
    },
    clearCurrentWarehouse: (state) => {
      state.currentWarehouse = null;
    },
    clearWarehouseInventory: (state) => {
      state.warehouseInventory = null;
    },
    clearWarehouseStats: (state) => {
      state.warehouseStats = null;
    },
    clearAllWarehouseData: (state) => {
      state.warehouses = [];
      state.currentWarehouse = null;
      state.warehouseInventory = null;
      state.warehouseStats = null;
      state.loading = false;
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch warehouses
      .addCase(fetchWarehouses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWarehouses.fulfilled, (state, action) => {
        state.loading = false;
        state.warehouses = action.payload;
      })
      .addCase(fetchWarehouses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch warehouse by ID
      .addCase(fetchWarehouseById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWarehouseById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentWarehouse = action.payload;
      })
      .addCase(fetchWarehouseById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create warehouse
      .addCase(createWarehouse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createWarehouse.fulfilled, (state, action) => {
        state.loading = false;
        state.warehouses.unshift(action.payload);
        state.success = 'Warehouse created successfully';
      })
      .addCase(createWarehouse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update warehouse
      .addCase(updateWarehouse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWarehouse.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.warehouses.findIndex(w => w.id === action.payload.id);
        if (index !== -1) {
          state.warehouses[index] = action.payload;
        }
        if (state.currentWarehouse && state.currentWarehouse.id === action.payload.id) {
          state.currentWarehouse = action.payload;
        }
        state.success = 'Warehouse updated successfully';
      })
      .addCase(updateWarehouse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete warehouse
      .addCase(deleteWarehouse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteWarehouse.fulfilled, (state, action) => {
        state.loading = false;
        // action.payload is the warehouseId (e.g., WH123456). Items have both id (Mongo _id) and warehouseId.
        state.warehouses = state.warehouses.filter(w => w.id !== action.payload && w.warehouseId !== action.payload);
        if (state.currentWarehouse && state.currentWarehouse.id === action.payload) {
          state.currentWarehouse = null;
        }
        state.success = 'Warehouse deleted successfully';
      })
      .addCase(deleteWarehouse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch warehouse inventory
      .addCase(fetchWarehouseInventory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWarehouseInventory.fulfilled, (state, action) => {
        state.loading = false;
        state.warehouseInventory = action.payload;
      })
      .addCase(fetchWarehouseInventory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Add product to warehouse
      .addCase(addProductToWarehouse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addProductToWarehouse.fulfilled, (state, action) => {
        state.loading = false;
        if (state.warehouseInventory && state.warehouseInventory.products) {
          state.warehouseInventory.products.push(action.payload.product);
          state.warehouseInventory.totalProducts = state.warehouseInventory.products.length;
          state.warehouseInventory.totalValue = state.warehouseInventory.products.reduce((sum, product) => sum + (product.price * product.quantity), 0);
        }
        state.success = 'Product added to warehouse successfully';
      })
      .addCase(addProductToWarehouse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update warehouse product
      .addCase(updateWarehouseProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWarehouseProduct.fulfilled, (state, action) => {
        state.loading = false;
        if (state.warehouseInventory && state.warehouseInventory.products) {
          const index = state.warehouseInventory.products.findIndex(p => p.id === action.payload.product.id);
          if (index !== -1) {
            state.warehouseInventory.products[index] = action.payload.product;
            // Recalculate totals
            state.warehouseInventory.totalValue = state.warehouseInventory.products.reduce((sum, product) => sum + (product.price * product.quantity), 0);
          }
        }
        state.success = 'Product updated successfully';
      })
      .addCase(updateWarehouseProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch warehouse stats
      .addCase(fetchWarehouseStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWarehouseStats.fulfilled, (state, action) => {
        state.loading = false;
        state.warehouseStats = action.payload;
      })
      .addCase(fetchWarehouseStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Bulk upload warehouse products
      .addCase(bulkUploadWarehouseProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUploadWarehouseProducts.fulfilled, (state, action) => {
        state.loading = false;
        // Add successful products to warehouse inventory
        if (state.warehouseInventory) {
          // Initialize products array if it doesn't exist
          if (!state.warehouseInventory.products) {
            state.warehouseInventory.products = [];
          }
          // Add successful products
          state.warehouseInventory.products.push(...action.payload.results.successful);
          // Recalculate totals
          state.warehouseInventory.totalProducts = state.warehouseInventory.products.length;
          state.warehouseInventory.totalValue = state.warehouseInventory.products.reduce((sum, product) => sum + (product.price * product.quantity), 0);
        }
        state.success = action.payload.message;
      })
      .addCase(bulkUploadWarehouseProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Bulk update warehouse inventory
      .addCase(bulkUpdateWarehouseInventory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdateWarehouseInventory.fulfilled, (state, action) => {
        state.loading = false;
        // Apply quantity changes locally for a quick UI update
        if (state.warehouseInventory && state.warehouseInventory.products && action.payload.updated) {
          const skuToUpdate = new Map(action.payload.updated.map(u => [u.sku, u]));
          state.warehouseInventory.products = state.warehouseInventory.products.map(p => {
            const upd = skuToUpdate.get(p.sku);
            if (upd) {
              return { ...p, quantity: upd.newQuantity };
            }
            return p;
          });
          // Recalculate totals
          state.warehouseInventory.totalProducts = state.warehouseInventory.products.length;
          state.warehouseInventory.totalValue = state.warehouseInventory.products.reduce((sum, product) => sum + (product.price * product.quantity), 0);
        }
        state.success = 'Warehouse inventory updated successfully';
      })
      .addCase(bulkUpdateWarehouseInventory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Listen for store changes and clear warehouse data
      .addMatcher(
        (action) => action.type === 'store/setSelectedStore' || action.type === 'store/clearStoreData',
        (state, action) => {
          // Clear warehouse data when store changes
          state.warehouses = [];
          state.currentWarehouse = null;
          state.warehouseInventory = null;
          state.warehouseStats = null;
          state.loading = false;
          state.error = null;
          state.success = null;
        }
      );
  },
});

export const {
  clearError,
  clearSuccess,
  setCurrentWarehouse,
  clearCurrentWarehouse,
  clearWarehouseInventory,
  clearWarehouseStats,
  clearAllWarehouseData,
} = warehouseSlice.actions;

export default warehouseSlice.reducer; 